/**
 * The conversation docked beside a dashboard.
 *
 * The thread is resolved by the server rather than remembered in the browser.
 * A remembered id lives on one machine, so the same person opening the same
 * dashboard elsewhere would silently start again with no way back to what they
 * asked yesterday — and a dashboard is a thing people return to.
 */

import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The thread for this dashboard, started if it does not exist yet. A board that
 * opens as more than one page passes its `view`, so each page keeps its own
 * conversation, as it keeps its own title and starters.
 */
export async function fetchDashboardChatThread(dashboardId: string, view?: string): Promise<string> {
  const tenantId = getTenantIdFromStorage();
  const token = window.localStorage.getItem('klaris_jwt');
  if (!tenantId || !token) throw new Error('You are signed out. Sign in again to use the chat.');

  const response = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/dashboards/${encodeURIComponent(dashboardId)}/chat-thread`
      + (view ? `?view=${encodeURIComponent(view)}` : '')),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new Error(`The dashboard chat could not be opened (${response.status}).`);
  }

  const data = await response.json();
  const threadId = String(data?.thread_id || '');
  if (!threadId) throw new Error('The dashboard chat could not be opened.');
  return threadId;
}
